import { useCallback, useEffect, useRef } from 'react'
import { useSearchContext } from '../context/SearchContext.js'
import { getChallenges, getMatchData, getChampionMastery, getSearchHistory, getTftMatchHistory } from '../api/index.js'

export function useSearch () {
  const { state, dispatch, actionTypes } = useSearchContext()
  const { summonerName, region, value, searchHistory, tftQueue, tftCount, tabCache } = state
  const lastTftSummonerRef = useRef('')

  const findParticipant = useCallback((fullSummonerName) => {
    const [playerName, hashTag] = fullSummonerName.includes('#') ? fullSummonerName.split('#') : [fullSummonerName, region]
    return searchHistory.find(a => a.playerName.toLowerCase() === playerName.toLowerCase() && a.region === region && a.hashTag === hashTag)
  }, [region, searchHistory])

  const setSummonerName = useCallback((name) => {
    dispatch({ type: actionTypes.SET_SUMMONER, payload: name })
  }, [dispatch, actionTypes])

  const setRegion = useCallback((nextRegion) => {
    dispatch({ type: actionTypes.SET_REGION, payload: nextRegion })
  }, [dispatch, actionTypes])

  const setMessage = useCallback((message) => {
    dispatch({ type: actionTypes.SET_MESSAGE, payload: message })
  }, [dispatch, actionTypes])

  const setData = useCallback((data) => {
    dispatch({ type: actionTypes.SET_DATA, payload: data })
  }, [dispatch, actionTypes])

  const setUserData = useCallback((data) => {
    dispatch({ type: actionTypes.SET_USER_DATA, payload: data })
  }, [dispatch, actionTypes])

  const setLoading = useCallback((isLoading) => {
    dispatch({ type: actionTypes.SET_LOADING, payload: isLoading })
  }, [dispatch, actionTypes])

  const setUpdate = useCallback((isUpdating) => {
    dispatch({ type: actionTypes.SET_UPDATE, payload: isUpdating })
  }, [dispatch, actionTypes])

  const setTab = useCallback((tabIndex) => {
    dispatch({ type: actionTypes.SET_TAB, payload: tabIndex })
  }, [dispatch, actionTypes])

  const setTftQueue = useCallback((queue) => {
    dispatch({ type: actionTypes.SET_TFT_QUEUE, payload: queue })
  }, [dispatch, actionTypes])

  const setTftCount = useCallback((count) => {
    dispatch({ type: actionTypes.SET_TFT_COUNT, payload: count })
  }, [dispatch, actionTypes])

  const setSearchHistory = useCallback((history) => {
    dispatch({ type: actionTypes.SET_SEARCH_HISTORY, payload: history })
  }, [dispatch, actionTypes])

  const fetchSearchHistory = useCallback(async () => {
    try {
      const history = await getSearchHistory()
      setSearchHistory(history)
    } catch (error) {
      // ignore for now
    }
  }, [setSearchHistory])

  useEffect(() => {
    fetchSearchHistory()
  }, [fetchSearchHistory])

  const fetchData = useCallback(async (fetchFunction, tabIndex) => {
    setLoading(true)
    try {
      const data = await fetchFunction()
      if (data && Object.keys(data).length > 0) {
        setData(data)
        dispatch({ type: actionTypes.SET_TAB_CACHE, payload: { tab: tabIndex, data } })
        setMessage('')
        return data
      } else {
        setData({})
        setMessage('No data available for the specified summoner name.')
        return null
      }
    } catch (error) {
      setData({})
      setMessage(error?.response?.data?.error?.status?.message || 'Failed to fetch data, please try again.')
      return null
    } finally {
      setLoading(false)
    }
  }, [dispatch, actionTypes, setData, setLoading, setMessage])

  const handleSearch = useCallback(async (isUpdate = false, options = {}) => {
    if (!summonerName) return
    const tftCountOverride = options?.tftCountOverride
    let effectiveTftCount = tftCount

    if (value === 0 && summonerName !== lastTftSummonerRef.current) {
      lastTftSummonerRef.current = summonerName
      effectiveTftCount = 10
      if (tftCount !== 10) {
        setTftCount(10)
      }
    }

    if (typeof tftCountOverride === 'number') {
      effectiveTftCount = tftCountOverride
    }

    const fetchFunctions = [
      () => getTftMatchHistory(summonerName, region, tftQueue, effectiveTftCount),
      () => getMatchData(summonerName, region, isUpdate),
      () => getChallenges(summonerName, region, isUpdate),
      () => getChampionMastery(summonerName, region, isUpdate)
    ]

    const data = await fetchData(fetchFunctions[value], value)

    if (value === 0 && data) {
      const participant = findParticipant(summonerName)
      setUserData({ participant })
    } else if (value !== 0) {
      const participant = findParticipant(summonerName)
      setUserData({ participant })
    }
  }, [fetchData, findParticipant, setTftCount, setUserData, summonerName, tftCount, tftQueue, value, region])

  const handleTabChange = useCallback((event, newValue) => {
    setTab(newValue)
    if (tabCache?.[newValue]) {
      setData(tabCache[newValue])
      setMessage('')
    } else {
      setData({})
      setMessage('When switching tabs, you need to search for the summoner again.')
    }
  }, [setData, setMessage, setTab, tabCache])

  const handleUpdate = useCallback(async () => {
    setUpdate(true)
    try {
      await handleSearch(true)
    } catch (error) {
      setMessage('Failed to update profile.')
    } finally {
      setUpdate(false)
    }
  }, [handleSearch, setMessage, setUpdate])

  return {
    state,
    actions: {
      setSummonerName,
      setRegion,
      setTftQueue,
      setTftCount,
      handleSearch,
      handleTabChange,
      handleUpdate
    }
  }
}
